/**
 * Luật thuần của màn Lịch sử luồng chạy (`instance.ts`, `error-report-rows.ts`, `robot-log.ts` của web-portal native).
 */
import type { BaoCaoLoi, DongLichSu, SuKienLoi, TepBangChung } from '../types';

/** `updatedAt` của instance chưa xong chỉ là lần ghi gần nhất, không phải lúc kết thúc. */
const CHUA_XONG = new Set(['running', 'idle']);

export function daXong(status: string): boolean {
  return !CHUA_XONG.has(status);
}

/** Cỡ trang máy chủ nhận (`CO_TRANG` của `lich-su.service.ts`). */
export const CO_TRANG_LICH_SU = [20, 50, 100] as const;

/** Trạng thái instance OpenRPA mà ô lọc đưa ra — các giá trị `state` OpenCore ghi. */
export const TRANG_THAI_INSTANCE = ['completed', 'failed', 'running', 'idle', 'aborted'] as const;

/** Một dòng của bảng báo cáo lỗi: một sự kiện lỗi và tệp bằng chứng của nó, hoặc dòng "tệp chung". */
export interface DongBaoCao {
  key: string;
  event?: SuKienLoi;
  chung?: boolean;
  tep: TepBangChung[];
}

const LOAI_HIEN: ReadonlySet<string> = new Set(['video', 'screenshot', 'log']);

/**
 * Gắn tệp vào sự kiện theo định danh (`eventId`, hoặc `screenshotAttachment` của ảnh), không theo thứ tự: một tệp gửi
 * thiếu hay đến muộn không làm tệp khác lệch sang lỗi khác. Tệp không gắn được sự kiện nào vào dòng "tệp chung".
 */
export function gomTepTheoLoi(bc: Pick<BaoCaoLoi, 'events' | 'attachments'>): DongBaoCao[] {
  const dong: DongBaoCao[] = bc.events.map((e) => ({ key: `event:${e.eventId}`, event: e, tep: [] }));
  const theoSuKien = new Map(dong.map((d) => [d.event!.eventId, d]));
  const chung: DongBaoCao = { key: 'shared', chung: true, tep: [] };
  for (const t of bc.attachments) {
    if (!LOAI_HIEN.has(t.kind)) continue;
    let d = t.eventId ? theoSuKien.get(t.eventId) : undefined;
    if (!d && t.kind === 'screenshot') d = dong.find((x) => x.event?.screenshotAttachment === t.id);
    if (!d && t.eventId) {
      d = { key: `event:${t.eventId}`, tep: [] };
      theoSuKien.set(t.eventId, d);
      dong.push(d);
    }
    (d ?? chung).tep.push(t);
  }
  if (chung.tep.length) dong.push(chung);
  return dong;
}

export type MucLog = 'error' | 'warning' | 'normal';

const NGAN_TRUONG = ' | ';
const VI_TRI_MUC = 2;
const MUC_THEO_CHU: Record<string, MucLog> = { ERROR: 'error', WARN: 'warning' };

/** Chia log thành dòng, tô theo mức ở trường thứ ba (`thời gian | runId | MỨC | …`); dòng lạ giữ màu thường. */
export function tachDongLog(log: string): { text: string; muc: MucLog }[] {
  return log.split('\n').map((text) => ({ text, muc: MUC_THEO_CHU[text.split(NGAN_TRUONG)[VI_TRI_MUC]?.trim() ?? ''] ?? 'normal' }));
}

const DAI_TEN_TOI_DA = 80;

function asciiAnToan(v: string): string {
  return v
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/đ/g, 'd')
    .replace(/Đ/g, 'D')
    .replace(/[^A-Za-z0-9._-]+/g, '_')
    .replace(/^[._-]+|[._-]+$/g, '')
    .slice(0, DAI_TEN_TOI_DA);
}

/** `<workflow>-<executionId>.log`, chỉ ký tự an toàn cho mọi hệ điều hành. */
export function tenTepLog(log: { workflowName?: string | null; executionId: string }): string {
  const phan = [asciiAnToan(log.workflowName ?? ''), asciiAnToan(log.executionId)].filter(Boolean);
  return `${phan.join('-') || 'robot-log'}.log`;
}

/**
 * Trang Dữ liệu thực thi của một dòng lịch sử; mang theo tên và trạng thái để tiêu đề không phải hỏi lại máy chủ, và
 * `executionId` Robot đã dùng (theo báo cáo lỗi hoặc log) để tra kết quả cào — kết quả cào chỉ gắn theo mã đó.
 */
export function duongDuLieu(
  d: Pick<DongLichSu, 'instanceId' | 'name' | 'workflowId' | 'status' | 'errorReport' | 'robotLog'>,
): string {
  const q = new URLSearchParams({ instanceId: d.instanceId, name: d.name || d.workflowId, status: d.status });
  const ma = d.errorReport?.executionId || d.robotLog?.executionId;
  if (ma) q.set('executionId', ma);
  return `/rpa/history/execution-data?${q.toString()}`;
}

/** Mã `RUN-…` của lượt Native: chỉ lượt đó mới có dòng thời gian ở `GET /runs/:runCode/logs`. */
export function laMaLuotNative(v: string | null | undefined): v is string {
  return typeof v === 'string' && /^RUN-/.test(v);
}
